import api from './api';
import offlineService from './offlineService';

export { offlineService };

export const authService = {
  async login(email, password) {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('user', JSON.stringify(user));
    return response.data;
  },

  async register(data) {
    const response = await api.post('/auth/register', data);
    const { access_token, user } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('user', JSON.stringify(user));
    return response.data;
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
  },

  getStoredUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  },

  async refreshUser() {
    // Fetch fresh user data from server and update localStorage
    try {
      const response = await api.get('/auth/me');
      const user = response.data;
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return null;
    }
  }
};

export const sitesService = {
  async list(params = {}) {
    const response = await api.get('/sites', { params });
    return response.data;
  },

  async get(id) {
    const response = await api.get(`/sites/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/sites', data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.put(`/sites/${id}`, data);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/sites/${id}`);
    return response.data;
  },

  async detachFromGroup(siteId) {
    const response = await api.post(`/sites/${siteId}/detach-from-group`);
    return response.data;
  },

  // Site Contacts API
  async listContacts(siteId) {
    const response = await api.get(`/sites/${siteId}/contacts`);
    return response.data;
  },

  async createContact(siteId, data) {
    const response = await api.post(`/sites/${siteId}/contacts`, data);
    return response.data;
  },

  async updateContact(siteId, contactId, data) {
    const response = await api.put(`/sites/${siteId}/contacts/${contactId}`, data);
    return response.data;
  },

  async deleteContact(siteId, contactId) {
    const response = await api.delete(`/sites/${siteId}/contacts/${contactId}`);
    return response.data;
  },

  // Phase 1A: Auditor-editable fields
  async updateAuditorFields(siteId, data) {
    const response = await api.patch(`/sites/${siteId}/auditor-fields`, data);
    return response.data;
  },

  // Stocktake Settings (Phase 3)
  async getStocktakeSettings(siteId) {
    const response = await api.get(`/sites/${siteId}/stocktake-settings`);
    return response.data;
  },

  async upsertStocktakeSettings(siteId, data) {
    const response = await api.put(`/sites/${siteId}/stocktake-settings`, data);
    return response.data;
  }
};

export const servicesService = {
  async list(params = {}) {
    const response = await api.get('/services', { params });
    return response.data;
  },

  async get(id) {
    const response = await api.get(`/services/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/services', data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.put(`/services/${id}`, data);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/services/${id}`);
    return response.data;
  }
};

export const clientsService = {
  async list(params = {}) {
    const response = await api.get('/clients', { params });
    return response.data;
  },

  async listGroups(params = {}) {
    const response = await api.get('/clients/groups', { params });
    return response.data;
  },

  async get(id) {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/clients', data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.put(`/clients/${id}`, data);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/clients/${id}`);
    return response.data;
  },

  async attachToGroup(clientId, groupId) {
    const response = await api.post(`/clients/${clientId}/attach-to-group?group_id=${groupId}`);
    return response.data;
  },

  async convertToGroup(clientId) {
    const response = await api.post(`/clients/${clientId}/convert-to-group`);
    return response.data;
  },

  // Search customers including child sites
  async search(query, params = {}) {
    const response = await api.get('/clients/search', { params: { q: query, ...params } });
    return response.data;
  },

  // Get child sites for a group client (for expand/collapse)
  async getSites(clientId) {
    const response = await api.get(`/clients/${clientId}/sites`);
    return response.data;
  },

  // Client Contacts Management
  async listContacts(clientId) {
    const response = await api.get(`/clients/${clientId}/contacts`);
    return response.data;
  },

  async createContact(clientId, data) {
    const response = await api.post(`/clients/${clientId}/contacts`, data);
    return response.data;
  },

  async updateContact(clientId, contactId, data) {
    const response = await api.put(`/clients/${clientId}/contacts/${contactId}`, data);
    return response.data;
  },

  async deleteContact(clientId, contactId) {
    const response = await api.delete(`/clients/${clientId}/contacts/${contactId}`);
    return response.data;
  },

  // Check if a colour is already in use by another client
  async checkColour(colour, excludeClientId = null) {
    const params = excludeClientId ? { exclude_client_id: excludeClientId } : {};
    const response = await api.get(`/clients/check-colour/${encodeURIComponent(colour)}`, { params });
    return response.data;
  }
};

export const settingsService = {
  async get() {
    const response = await api.get('/settings');
    return response.data;
  },

  async update(data) {
    const response = await api.put('/settings', data);
    return response.data;
  },

  async getBankHolidays(params = {}) {
    const response = await api.get('/settings/bank-holidays', { params });
    return response.data;
  },

  async getOrganisation() {
    const response = await api.get('/settings/organisation');
    return response.data;
  },

  async updateOrganisation(data) {
    const response = await api.put('/settings/organisation', data);
    return response.data;
  },

  async updateOrganisationLogo(logoUrl) {
    const response = await api.put('/settings/organisation/logo', { logo_url: logoUrl });
    return response.data;
  },

  async uploadOrganisationLogo(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/settings/organisation/logo/upload', formData);
    return response.data;
  }
};

export const reportsService = {
  async getDashboardStats(params = {}) {
    const response = await api.get('/reports/dashboard-stats', { params });
    return response.data;
  },

  async getBusinessPulse() {
    const response = await api.get('/reports/business-pulse');
    return response.data;
  },

  async getPerformanceOverview(params = {}) {
    const response = await api.get('/reports/performance-overview', { params });
    return response.data;
  },

  async getPerformanceDrilldown(params = {}) {
    const response = await api.get('/reports/performance-overview/drilldown', { params });
    return response.data;
  },

  async getDashboardDrilldown(params = {}) {
    const response = await api.get('/reports/dashboard-stats/drilldown', { params });
    return response.data;
  },

  async getAuditorUsers() {
    const response = await api.get('/reports/auditor-users');
    return response.data;
  },

  async getKPI(params) {
    const response = await api.get('/reports/kpi', { params });
    return response.data;
  },

  async getKPIReport(params) {
    const response = await api.get('/reports/kpi', { params });
    return response.data;
  },

  async getPreparationQuality(params) {
    const response = await api.get('/reports/preparation-quality', { params });
    return response.data;
  },

  async exportKPIReport(params) {
    const response = await api.get('/reports/kpi/export', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // JOB-DRIVEN Forecast Report - canonical source of truth
  async getForecastReport(params) {
    const response = await api.get('/reports/forecast', { params });
    return response.data;
  },

  async exportForecastReport(params) {
    const response = await api.get('/reports/forecast/export', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // Schedule Report - same canonical data as forecast
  async getScheduleReport(params) {
    const response = await api.get('/reports/schedule', { params });
    return response.data;
  },

  async exportScheduleReport(params) {
    const response = await api.get('/reports/schedule/export', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // Audit Frequency Compliance Report
  async getAuditFrequencyCompliance(params) {
    const response = await api.get('/reports/audit-frequency-compliance', { params });
    return response.data;
  },

  async exportAuditFrequencyCompliancePDF(params) {
    const response = await api.get('/reports/audit-frequency-compliance/pdf', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // Job Tracker PDF Export
  async exportJobsTrackerPDF(params) {
    const response = await api.get('/reports/jobs-tracker/pdf', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // Schedule Matrix PDF Export
  async exportScheduleMatrixPDF(params) {
    const response = await api.get('/reports/schedule-matrix/pdf', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // Revenue Forecast PDF Export
  async exportRevenueForecastPDF(params) {
    const response = await api.get('/reports/revenue-forecast/pdf', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  async getCustomerSiteDirectory(params = {}) {
    const response = await api.get('/reports/customer-site-directory', { params });
    return response.data;
  },
  async exportCustomerSiteDirectoryCSV(params = {}) {
    const response = await api.get('/reports/customer-site-directory/export', { params, responseType: 'blob' });
    return response.data;
  },
  async exportCustomerSiteDirectoryPDF(params = {}) {
    const response = await api.get('/reports/customer-site-directory/pdf', { params, responseType: 'blob' });
    return response.data;
  }
};

export const auditorsService = {
  async list(params = {}) {
    const response = await api.get('/auditors', { params });
    return response.data;
  },

  async get(id) {
    const response = await api.get(`/auditors/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/auditors', data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.put(`/auditors/${id}`, data);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/auditors/${id}`);
    return response.data;
  }
};

export const jobsService = {
  async list(params = {}) {
    const response = await api.get('/jobs', { params });
    return response.data;
  },

  async get(id) {
    const response = await api.get(`/jobs/${id}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/jobs', data);
    return response.data;
  },

  async update(id, data) {
    const response = await api.put(`/jobs/${id}`, data);
    return response.data;
  },

  async updateStatus(id, status, completedOn = null, delayJustified = false, delayJustificationNote = null) {
    const payload = { status };
    if (completedOn) {
      payload.completed_on = completedOn;
    }
    if (delayJustified !== undefined) {
      payload.delay_justified = delayJustified;
    }
    if (delayJustificationNote) {
      payload.delay_justification_note = delayJustificationNote;
    }
    const response = await api.patch(`/jobs/${id}/status`, payload);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/jobs/${id}`);
    return response.data;
  },

  // Job Evidence methods
  async listEvidence(jobId) {
    const response = await api.get(`/jobs/${jobId}/evidence`);
    return response.data;
  },

  async addEvidence(jobId, data) {
    const response = await api.post(`/jobs/${jobId}/evidence`, data);
    return response.data;
  },

  // iter340 v8e — Lock toggle (Senior Scheduler / Admin only).
  async setLock(jobId, { is_locked, lock_reason } = {}) {
    const body = { is_locked: !!is_locked };
    if (lock_reason !== undefined) body.lock_reason = lock_reason;
    const response = await api.put(`/jobs/${jobId}/lock`, body);
    return response.data;
  }
};

export const notificationsService = {
  async getStatus() {
    const response = await api.get('/notifications/status');
    return response.data;
  },

  async getPreview(params) {
    const response = await api.get('/notifications/preview', { params });
    return response.data;
  },

  async publish(data) {
    const response = await api.post('/notifications/publish', data);
    return response.data;
  },

  async detectChanges(params) {
    const response = await api.get('/notifications/changes', { params });
    return response.data;
  },

  async sendUpdates(data) {
    const response = await api.post('/notifications/send-updates', data);
    return response.data;
  },

  async getLogs(params = {}) {
    const response = await api.get('/notifications/logs', { params });
    return response.data;
  },
  
  async getBatchDetails(batchId) {
    const response = await api.get(`/notifications/logs/${batchId}`);
    return response.data;
  },
  
  async deleteBatch(batchId) {
    const response = await api.delete(`/notifications/logs/${batchId}`);
    return response.data;
  },

  async markNotified(data) {
    const response = await api.post('/notifications/mark-notified', data);
    return response.data;
  },

  async markSingleNotified(jobId) {
    const response = await api.post(`/notifications/mark-single/${jobId}`);
    return response.data;
  },

  async getSchedulerStatus() {
    const response = await api.get('/scheduler/status');
    return response.data;
  },

  // Get client publication preview - returns sites with jobs and their contacts
  async getClientPublishPreview(params) {
    const response = await api.get('/notifications/publish-preview', { params });
    return response.data;
  }
};

// Calendar Job Notification digest rules (configurable automated email summaries)
export const calendarNotificationsService = {
  async list() {
    const response = await api.get('/calendar-notifications');
    return response.data.rules;
  },

  async getRecipients() {
    const response = await api.get('/calendar-notifications/recipients');
    return response.data.users;
  },

  async getTimezones() {
    const response = await api.get('/calendar-notifications/timezones');
    return response.data;
  },

  async create(data) {
    const response = await api.post('/calendar-notifications', data);
    return response.data;
  },

  async update(ruleId, data) {
    const response = await api.put(`/calendar-notifications/${ruleId}`, data);
    return response.data;
  },

  async remove(ruleId) {
    const response = await api.delete(`/calendar-notifications/${ruleId}`);
    return response.data;
  },

  async sendNow(ruleId) {
    const response = await api.post(`/calendar-notifications/${ruleId}/send-now`);
    return response.data;
  },

  async getLogs(ruleId) {
    const response = await api.get(`/calendar-notifications/${ruleId}/logs`);
    return response.data.logs;
  }
};

// Site Pricing Service - Multi-line pricing per site
export const sitePricingService = {
  async getForSite(siteId, serviceId = null) {
    const params = serviceId ? { service_id: serviceId } : {};
    const response = await api.get(`/site-pricing/site/${siteId}`, { params });
    return response.data;
  },

  async getDefault(siteId, serviceId) {
    const response = await api.get(`/site-pricing/default/${siteId}/${serviceId}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/site-pricing', data);
    return response.data;
  },

  async update(pricingId, data) {
    const response = await api.put(`/site-pricing/${pricingId}`, data);
    return response.data;
  },

  async delete(pricingId) {
    const response = await api.delete(`/site-pricing/${pricingId}`);
    return response.data;
  },

  async setDefault(pricingId) {
    const response = await api.post(`/site-pricing/set-default/${pricingId}`);
    return response.data;
  }
};

// Job Pricing Lines Service - Editable pricing on existing jobs
export const jobPricingService = {
  async getForJob(jobId) {
    const response = await api.get(`/batch-schedule/job/${jobId}/pricing-lines`);
    return response.data;
  },

  async addLine(jobId, data) {
    const response = await api.post(`/batch-schedule/job/${jobId}/pricing-lines`, data);
    return response.data;
  },

  async updateLine(jobId, lineId, data) {
    const response = await api.put(`/batch-schedule/job/${jobId}/pricing-lines/${lineId}`, data);
    return response.data;
  },

  async deleteLine(jobId, lineId) {
    const response = await api.delete(`/batch-schedule/job/${jobId}/pricing-lines/${lineId}`);
    return response.data;
  }
};

// Job Reports Service (Phase 2.1A/2.1C) - Client Reports Management
export const jobReportsService = {
  // List all reports for a job
  async listForJob(jobId) {
    const response = await api.get(`/jobs/${jobId}/reports`);
    return response.data;
  },

  // Upload a new report or new version
  async upload(jobId, file, serviceType, reportType, visibilityScope = 'internal_only', notes = null, saveAsDraft = false) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('service_type', serviceType);
    formData.append('report_type', reportType);
    formData.append('visibility_scope', visibilityScope);
    formData.append('save_as_draft', saveAsDraft);
    if (notes) {
      formData.append('notes', notes);
    }
    const response = await api.post(`/jobs/${jobId}/reports/upload`, formData);
    return response.data;
  },

  // Get a single report
  async get(reportId) {
    const response = await api.get(`/reports/${reportId}`);
    return response.data;
  },

  // Get all versions of a report
  async getVersions(reportId, includeDeleted = false) {
    const response = await api.get(`/reports/${reportId}/versions`, {
      params: { include_deleted: includeDeleted }
    });
    return response.data;
  },

  // Publish a report (DIRECT mode or Admin/Scheduler)
  async publish(reportId, notes = null) {
    const response = await api.post(`/reports/${reportId}/publish`, { notes });
    return response.data;
  },

  // Unpublish a report
  async unpublish(reportId, notes = null) {
    const response = await api.post(`/reports/${reportId}/unpublish`, { notes });
    return response.data;
  },

  // Request publish (for Auditors in APPROVAL_REQUIRED mode)
  async requestPublish(reportId, notes = null) {
    const response = await api.post(`/reports/${reportId}/request_publish`, { notes });
    return response.data;
  },

  // Approve & publish (for Admin/Scheduler in APPROVAL_REQUIRED mode)
  async approvePublish(reportId, notes = null) {
    const response = await api.post(`/reports/${reportId}/approve_publish`, { notes });
    return response.data;
  },

  // Get workflow settings
  async getWorkflowSettings() {
    const response = await api.get('/reports/settings/workflow');
    return response.data;
  }
};

// Client Access Service - Phase 2.2B
export const clientAccessService = {
  async listGroups() {
    const response = await api.get('/admin/groups');
    return response.data;
  },
  async listSites(params = {}) {
    const response = await api.get('/admin/sites', { params });
    return response.data;
  },
  async getAccess(userId) {
    const response = await api.get(`/admin/client-users/${userId}/access`);
    return response.data;
  },
  async updateAccess(userId, groupIds, subRegionIds, siteIds) {
    const response = await api.put(`/admin/client-users/${userId}/access`, {
      group_ids: groupIds,
      sub_region_ids: subRegionIds || [],
      site_ids: siteIds
    });
    return response.data;
  }
};

// Client Sub-Regions Service
export const clientSubRegionsService = {
  async list(clientId) {
    const response = await api.get('/client-sub-regions', { params: { client_id: clientId } });
    return response.data;
  },
  async create(data) {
    const response = await api.post('/client-sub-regions', data);
    return response.data;
  },
  async update(id, data) {
    const response = await api.put(`/client-sub-regions/${id}`, data);
    return response.data;
  },
  async remove(id) {
    const response = await api.delete(`/client-sub-regions/${id}`);
    return response.data;
  },
  async updateSites(id, siteIds) {
    const response = await api.put(`/client-sub-regions/${id}/sites`, { site_ids: siteIds });
    return response.data;
  },
};

// Client Reports Service - Phase 2.3A (Client Portal)
export const clientReportsService = {
  // List sites accessible to the client user
  async listSites() {
    const response = await api.get('/client/reports/sites');
    return response.data;
  },

  // List completed jobs for a site
  async listSiteJobs(siteId, params = {}) {
    const response = await api.get(`/client/reports/site/${siteId}/jobs`, { params });
    return response.data;
  },

  // List published reports for a job
  async listJobReports(jobId) {
    const response = await api.get(`/client/reports/job/${jobId}`);
    return response.data;
  },

  // List published summary reports for a job
  async listJobSummaryReports(jobId) {
    const response = await api.get(`/client/reports/job/${jobId}/summary-reports`);
    return response.data;
  },

  // Download a summary report PDF
  async downloadSummaryReportPdf(reportId, filename) {
    try {
      const response = await api.get(`/client/summary-reports/${reportId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `Summary_Report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error("You don't have access to this report.");
      }
      if (error.response?.status === 404) {
        throw new Error("Report file not found.");
      }
      throw new Error("Failed to download report. Please try again.");
    }
  },

  // Download a report version (backend proxies the download)
  async downloadReport(versionId, filename) {
    try {
      // Backend now proxies the download directly (no redirect)
      // Note: endpoint is /client/ not /client-portal/
      const response = await api.get(`/client/reports/version/${versionId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link from blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'report.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error("You don't have access to this report.");
      }
      if (error.response?.status === 404) {
        throw new Error("Report file not found.");
      }
      if (error.response?.status === 503) {
        throw new Error("Storage service unavailable. Please try again later.");
      }
      throw new Error("Something went wrong. Please try again.");
    }
  }
};

// Client Upcoming Audits Service - Phase 2.3B (Client Portal)
export const clientUpcomingAuditsService = {
  // List upcoming audits within client's access scope
  async listUpcomingAudits(params = {}) {
    const queryParams = {};
    if (params.from) queryParams.from = params.from;
    if (params.to) queryParams.to = params.to;
    if (params.limit) queryParams.limit = params.limit;
    
    const response = await api.get('/client/upcoming-audits', { params: queryParams });
    return response.data;
  }
};

// Client Portfolio Service - Multi-Site Portfolio Enhancement
export const clientPortfolioService = {
  // Get portfolio summary (overview, aggregates, site KPIs)
  async getPortfolioSummary(scope = null) {
    const params = scope ? { scope } : {};
    const response = await api.get('/client/portfolio/summary', { params });
    return response.data;
  },

  // Get upcoming audits for portfolio dashboard
  async getUpcomingAudits(limit = 5, subRegionId = null) {
    const params = { limit };
    if (subRegionId && subRegionId !== 'all') params.sub_region_id = subRegionId;
    const response = await api.get('/client/portfolio/upcoming-audits', { params });
    return response.data;
  },

  // Get regional performance trend (line chart data per sub-region)
  async getRegionalTrends(scope = null, limit = 6) {
    const params = { limit };
    if (scope) params.scope = scope;
    const response = await api.get('/client/portfolio/regional-trends', { params });
    return response.data;
  },

  // Get site comparison data for charts
  async getSiteComparison(scope = null) {
    const params = scope ? { scope } : {};
    const response = await api.get('/client/portfolio/site-comparison', { params });
    return response.data;
  },

  // Get sub-regions available to the current client user
  async getSubRegions() {
    const response = await api.get('/client/portfolio/sub-regions');
    return response.data;
  },

  // Export portfolio as CSV (optionally filtered by sub-region)
  async exportCSV(scope = null, subRegionId = null) {
    const params = {};
    if (scope) params.scope = scope;
    if (subRegionId && subRegionId !== 'all') params.sub_region_id = subRegionId;
    const response = await api.get('/client/portfolio/export-csv', { params, responseType: 'blob' });
    return response.data;
  }
};

// Client Portal Dashboard Layouts (Settings → Client Portal → Dashboard Layouts)
export const clientDashboardPreferencesService = {
  async getCatalogue() {
    const r = await api.get('/client-portal/dashboard-preferences/catalogue');
    return r.data;
  },
  async list() {
    const r = await api.get('/client-portal/dashboard-preferences');
    return r.data;
  },
  async upsert(payload) {
    const r = await api.put('/client-portal/dashboard-preferences', payload);
    return r.data;
  },
  async bulkUpsert(payload) {
    const r = await api.put('/client-portal/dashboard-preferences/bulk', payload);
    return r.data;
  },
  async resetScope(scope_type, scope_id = null) {
    const params = { scope_type };
    if (scope_id) params.scope_id = scope_id;
    const r = await api.post('/client-portal/dashboard-preferences/reset', null, { params });
    return r.data;
  },
  async copyUserOverrides(source_user_id, target_user_ids) {
    const r = await api.post('/client-portal/dashboard-preferences/copy-user', { source_user_id, target_user_ids });
    return r.data;
  },
};

// Reports Repository Service - Phase 2.6A (Internal)
export const reportsRepositoryService = {
  // Get filter options for dropdowns
  async getFilters() {
    const response = await api.get('/reports/repository/filters');
    return response.data;
  },

  // Search reports repository
  async search(params) {
    const response = await api.post('/reports/repository/search', params);
    return response.data;
  },

  // Get versions for a report
  async getVersions(reportId, showArchived = false) {
    const response = await api.get(`/reports/repository/report/${reportId}/versions`, {
      params: { show_archived: showArchived }
    });
    return response.data;
  },

  // Download a report version (backend proxies the download)
  async downloadVersion(versionId, filename) {
    try {
      // Backend now proxies the download directly (no redirect)
      const response = await api.get(`/reports/repository/version/${versionId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link from blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'report.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error("You don't have access to this report.");
      }
      if (error.response?.status === 404) {
        throw new Error("Report file not found. This file may need to be re-uploaded.");
      }
      if (error.response?.status === 503) {
        throw new Error("Storage service unavailable. Please try again later.");
      }
      throw new Error("Something went wrong. Please try again.");
    }
  },
  
  // Archive a report version (Admin/Scheduler only)
  async archiveVersion(versionId, reason) {
    const response = await api.post(`/reports/repository/version/${versionId}/archive`, {
      reason
    });
    return response.data;
  },
  
  // Restore an archived report version (Admin only)
  async restoreVersion(versionId, reason = null) {
    const response = await api.post(`/reports/repository/version/${versionId}/restore`, {
      reason
    });
    return response.data;
  },
  
  // Hard delete a report version (Admin only, one-time cleanup)
  async hardDeleteVersion(versionId) {
    const response = await api.delete(`/reports/repository/version/${versionId}/hard-delete`);
    return response.data;
  },
  
  // Delete all reports for a job (Admin only) - job remains in schedule
  async deleteAllJobReports(jobId) {
    const response = await api.delete(`/reports/repository/job/${jobId}/all-reports`);
    return response.data;
  }
};

// Client Impersonation Service - Phase 2.6B (Admin)
export const impersonationService = {
  // Start impersonation session for a client user
  async startImpersonation(clientUserId) {
    const response = await api.post(`/admin/impersonation/start/${clientUserId}`);
    return response.data;
  },

  // Start auditor view session (admin only)
  async startAuditorView(auditorUserId) {
    const response = await api.post(`/admin/impersonation/start-auditor/${auditorUserId}`);
    return response.data;
  },

  // End current impersonation / auditor-view session
  async endImpersonation() {
    const response = await api.post('/admin/impersonation/end');
    return response.data;
  },

  // Get current impersonation session (if any) — returns session_type
  async getCurrentImpersonation() {
    const response = await api.get('/admin/impersonation/current');
    return response.data;
  },

  // Revoke a specific session
  async revokeSession(sessionId) {
    const response = await api.post(`/admin/impersonation/revoke/${sessionId}`);
    return response.data;
  }
};


// AP Notifications toggle service
export const apNotificationsService = {
  async toggle(enabled) {
    const response = await api.patch('/settings/ap-notifications-toggle', { enabled });
    return response.data;
  },
};


// Dummy AP service
export const dummyApService = {
  async markDummy(planId, reason) {
    const response = await api.post(`/action-plans/${planId}/mark-dummy`, { reason });
    return response.data;
  },

  async purgeDummy(apIds, reason) {
    const response = await api.post('/action-plans/purge-dummy', {
      ap_ids: apIds,
      confirmation: 'PURGE DUMMY APS',
      reason,
    });
    return response.data;
  },
};

// Summary Stock Audit Reports Service - Phase 2A + Scope Update
export const summaryReportsService = {
  // Check job eligibility for Summary Report (returns eligible scopes)
  async checkEligibility(jobId) {
    const response = await api.get(`/summary-reports/jobs/${jobId}/eligibility`);
    return response.data;
  },

  // Get Summary Reports by Job ID (returns 0-2 reports for FOOD/BEVERAGE scopes)
  async getByJob(jobId, scope = null) {
    const params = scope ? { scope } : {};
    const response = await api.get(`/summary-reports/jobs/${jobId}`, { params });
    return response.data;
  },

  // Create Summary Report for a job with specific scope
  async create(jobId, data = {}) {
    // data should include { scope: 'FOOD' or 'BEVERAGE', ... }
    const response = await api.post(`/summary-reports/jobs/${jobId}`, data);
    return response.data;
  },

  // Get Summary Report by ID
  async get(reportId) {
    const response = await api.get(`/summary-reports/${reportId}`);
    return response.data;
  },

  // P3 — read-only link status for the Wizard banner (Scope B)
  async getLinkStatus(reportId) {
    const response = await api.get(`/summary-reports/${reportId}/link-status`);
    return response.data;
  },

  // Scope C (draft-first) — explicit, auditor-triggered re-bind to the
  // current snapshot version after a recalculation. Never automatic.
  async refreshSource(reportId) {
    const response = await api.post(`/summary-reports/${reportId}/refresh-source`);
    return response.data;
  },

  // Update Summary Report (DRAFT only)
  async update(reportId, data) {
    const response = await api.put(`/summary-reports/${reportId}`, data);
    return response.data;
  },

  // Delete Summary Report (Admin/Scheduler only, DRAFT only)
  async delete(reportId) {
    await api.delete(`/summary-reports/${reportId}`);
  },

  // Publish Summary Report
  async publish(reportId) {
    const response = await api.post(`/summary-reports/${reportId}/publish`);
    return response.data;
  },

  // Unpublish Summary Report (Scheduler/Admin only)
  async unpublish(reportId) {
    const response = await api.post(`/summary-reports/${reportId}/unpublish`);
    return response.data;
  },

  // Schedule Next Audit (Phase 2A Patch) - now scope-aware
  async scheduleNextAudit(reportId, data) {
    const response = await api.post(`/summary-reports/${reportId}/schedule-next-audit`, data);
    return response.data;
  },

  // Export PDF
  async exportPdf(reportId) {
    const response = await api.get(`/summary-reports/${reportId}/pdf`, {
      responseType: 'blob'
    });
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Summary_Report_${reportId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // List Summary Reports (for repository) - supports scope filter
  async list(params = {}) {
    const response = await api.get('/summary-reports', { params });
    return response.data;
  },

  async getPriorKpis(siteId, scope, excludeReportId = null) {
    const params = {};
    if (excludeReportId) params.exclude_report_id = excludeReportId;
    const response = await api.get(`/summary-reports/prior-kpis/${siteId}/${scope}`, { params });
    return response.data;
  },

  async getPriorSectionItems(siteId, scope, excludeReportId = null) {
    const params = {};
    if (excludeReportId) params.exclude_report_id = excludeReportId;
    const response = await api.get(`/summary-reports/prior-section-items/${siteId}/${scope}`, { params });
    return response.data;
  },

  async getStockAuditAttachment(reportId) {
    const response = await api.get(`/summary-reports/${reportId}/stock-audit-attachment`);
    return response.data;
  },

  async generateStockAuditAttachment(reportId) {
    const response = await api.post(`/summary-reports/${reportId}/stock-audit-attachment/generate`);
    return response.data;
  },

  async getPriorYtd(siteId, scope, excludeReportId = null) {
    const params = {};
    if (excludeReportId) params.exclude_report_id = excludeReportId;
    const response = await api.get(`/summary-reports/prior-ytd/${siteId}/${scope}`, { params });
    return response.data;
  },

  // 2026-11 — explicit, reviewer-triggered recalculation of the three
  // system-only Opening YTD amount fields after a site year-end change.
  async recalculateOpeningYtd(reportId) {
    const response = await api.post(`/summary-reports/${reportId}/recalculate-opening-ytd`);
    return response.data;
  }
};


// ── Client Report Metrics Templates Service ──────────────────────────

export const metricsTemplatesService = {
  async list(includeArchived = false) {
    const response = await api.get('/metrics-templates/', { params: { include_archived: includeArchived } });
    return response.data;
  },

  async create(data) {
    const response = await api.post('/metrics-templates/', data);
    return response.data;
  },

  async update(templateId, data) {
    const response = await api.put(`/metrics-templates/${templateId}`, data);
    return response.data;
  },

  async archive(templateId) {
    const response = await api.post(`/metrics-templates/${templateId}/archive`);
    return response.data;
  },

  async assign(templateId, assignmentType, entityId) {
    const response = await api.post(`/metrics-templates/${templateId}/assign`, {
      assignment_type: assignmentType,
      entity_id: entityId,
    });
    return response.data;
  },

  async batchAssign(templateId, assignmentType, entityIds) {
    const response = await api.post(`/metrics-templates/${templateId}/assign-batch`, {
      assignment_type: assignmentType,
      entity_ids: entityIds,
    });
    return response.data;
  },

  async removeAssignment(assignmentId) {
    await api.delete(`/metrics-templates/assignments/${assignmentId}`);
  },

  async resolve(siteId) {
    const response = await api.get(`/metrics-templates/resolve/${siteId}`);
    return response.data;
  },

  async getCapturedMetrics(reportId) {
    const response = await api.get(`/metrics-templates/captured/${reportId}`);
    return response.data;
  },

  async saveCapturedMetrics(reportId, data) {
    const response = await api.post(`/metrics-templates/captured/${reportId}`, data);
    return response.data;
  },
};


// ── Beverage Stock Master ────────────────────────────────────────
export const beverageProductsService = {
  async list(params = {}) {
    const response = await api.get('/beverage-products', { params });
    return response.data;
  },

  /** Fetch ALL products by paginating (page_size=500). Returns flat array. */
  async listAll(extraParams = {}) {
    const pageSize = 500;
    let page = 1;
    let allItems = [];
    let hasMore = true;
    while (hasMore) {
      const response = await api.get('/beverage-products', {
        params: { ...extraParams, page, page_size: pageSize },
      });
      const data = response.data;
      const items = data.items || [];
      allItems = allItems.concat(items);
      const total = data.total || 0;
      hasMore = allItems.length < total;
      page++;
      if (page > 50) break; // safety: max 25,000 products
    }
    return allItems;
  },

  /**
   * iter341 — light variant of `listAll` for product pickers / search
   * modals that don't need per-product units + yields. Backend skips
   * the heavy selectinload joins which makes this ~3x faster and ~5x
   * smaller. Use this for "Add from master" / autocomplete pickers
   * where the auditor only sees name/code/brand/category.
   */
  async listAllLite(extraParams = {}) {
    const pageSize = 1000;
    let page = 1;
    let allItems = [];
    let hasMore = true;
    while (hasMore) {
      const response = await api.get('/beverage-products', {
        params: { ...extraParams, page, page_size: pageSize, lite: true },
      });
      const data = response.data;
      const items = data.items || [];
      allItems = allItems.concat(items);
      const total = data.total || 0;
      hasMore = allItems.length < total;
      page++;
      if (page > 50) break;
    }
    return allItems;
  },

  async get(productId) {
    const response = await api.get(`/beverage-products/${productId}`);
    return response.data;
  },

  async create(data) {
    const response = await api.post('/beverage-products', data);
    return response.data;
  },

  async update(productId, data) {
    const response = await api.put(`/beverage-products/${productId}`, data);
    return response.data;
  },

  async delete(productId) {
    await api.delete(`/beverage-products/${productId}`);
  },

  // Stage 1A — duplicate review (advisory) and read-only impact review
  async duplicateCheck(params) {
    const response = await api.get('/beverage-products/meta/duplicate-check', { params });
    return response.data;
  },

  async impactReview(productId, data) {
    const response = await api.post(`/beverage-products/${productId}/impact-review`, data);
    return response.data;
  },

  async getCategories() {
    const response = await api.get('/beverage-products/meta/categories');
    return response.data;
  },

  async importXlsx(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/beverage-products/import-xlsx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data;
  },

  async importStatus(taskId) {
    const response = await api.get(`/beverage-products/import-status/${taskId}`);
    return response.data;
  },
};


// ── Site Inventory, Locations, Sheets ────────────────────────────
export const siteInventoryService = {
  async listInventory(siteId) {
    const response = await api.get(`/sites/${siteId}/beverage-inventory`);
    return response.data;
  },
  // iter340 v5 — site-level measure / yield overrides for the new
  // Site Inventory Setup floating window in the wizard header.
  async getProductMeasures(siteId) {
    const response = await api.get(`/stocktake/sites/${siteId}/product-measures`);
    return response.data;
  },
  // iter340 v6 — inline edit of site override from the floating window.
  async upsertProductMeasure(siteId, productId, payload) {
    const response = await api.put(
      `/stocktake/sites/${siteId}/product-measures/${productId}`,
      payload
    );
    return response.data;
  },
  async deleteProductMeasure(siteId, productId) {
    const response = await api.delete(
      `/stocktake/sites/${siteId}/product-measures/${productId}`
    );
    return response.data;
  },
  async bulkSaveInventory(siteId, items) {
    const response = await api.put(`/sites/${siteId}/beverage-inventory`, { items });
    return response.data;
  },
  async addInventoryItem(siteId, data) {
    const response = await api.post(`/sites/${siteId}/beverage-inventory/add`, data);
    return response.data;
  },
  async removeInventoryItem(siteId, productId) {
    await api.delete(`/sites/${siteId}/beverage-inventory/${productId}`);
  },
  async setCaseOverride(siteId, productId, payload) {
    const response = await api.patch(
      `/sites/${siteId}/beverage-inventory/${productId}/case-override`,
      payload
    );
    return response.data;
  },

  async listLocations(siteId) {
    const response = await api.get(`/sites/${siteId}/beverage-locations`);
    return response.data;
  },
  async bulkSaveLocations(siteId, locations) {
    const response = await api.put(`/sites/${siteId}/beverage-locations`, { locations });
    return response.data;
  },

  async listTemplates(siteId) {
    const response = await api.get(`/sites/${siteId}/count-sheet-templates`);
    return response.data;
  },
  async createTemplate(siteId, data) {
    const response = await api.post(`/sites/${siteId}/count-sheet-templates`, data);
    return response.data;
  },
  async updateTemplate(siteId, templateId, data) {
    const response = await api.put(`/sites/${siteId}/count-sheet-templates/${templateId}`, data);
    return response.data;
  },
  async deleteTemplate(siteId, templateId) {
    await api.delete(`/sites/${siteId}/count-sheet-templates/${templateId}`);
  },
  async generateSheet(siteId, templateId) {
    const response = await api.post(`/sites/${siteId}/count-sheet-templates/${templateId}/generate`);
    return response.data;
  },

  // ── Phase 3: Site Product Measures (Overrides) ──
  async getProductMeasures(siteId) {
    const response = await api.get(`/stocktake/sites/${siteId}/product-measures`);
    return response.data;
  },
  async upsertProductMeasure(siteId, productId, data) {
    const response = await api.put(`/stocktake/sites/${siteId}/product-measures/${productId}`, data);
    return response.data;
  },
  async deleteProductMeasure(siteId, productId) {
    await api.delete(`/stocktake/sites/${siteId}/product-measures/${productId}`);
  },

  // ── Phase 3: Site Product Prices (Effective Dating) ──
  async getProductPrices(siteId, productId = null) {
    const params = productId ? { product_id: productId } : {};
    const response = await api.get(`/stocktake/sites/${siteId}/product-prices`, { params });
    return response.data;
  },
  async createProductPrice(siteId, productId, data) {
    const response = await api.post(`/stocktake/sites/${siteId}/product-prices/${productId}`, data);
    return response.data;
  },
  async updateProductPrice(siteId, priceId, data) {
    const response = await api.put(`/stocktake/sites/${siteId}/product-prices/${priceId}`, data);
    return response.data;
  },
  async deleteProductPrice(siteId, priceId) {
    await api.delete(`/stocktake/sites/${siteId}/product-prices/${priceId}`);
  },
  async getEffectivePrice(siteId, productId, periodStart, periodEnd) {
    const response = await api.get(`/stocktake/sites/${siteId}/product-prices/${productId}/effective`, {
      params: { period_start: periodStart, period_end: periodEnd }
    });
    return response.data;
  },
};

// ── Admin Operations (Backup, Recovery & Maintenance) ───────────────────────
export const adminOpsService = {
  async configStatus() {
    const r = await api.get('/admin/ops/config-status');
    return r.data;
  },
  async history(opType = null, limit = 50) {
    const params = { limit };
    if (opType) params.op_type = opType;
    const r = await api.get('/admin/ops/history', { params });
    return r.data;
  },
  // Environment Sync
  async syncStatus() {
    const r = await api.get('/admin/ops/sync/status');
    return r.data;
  },
  async refreshStaging(confirmationText) {
    const r = await api.post('/admin/ops/sync/refresh-staging', { confirmation_text: confirmationText });
    return r.data;
  },
  // Database Backups
  async dbInfo() {
    const r = await api.get('/admin/ops/backups/db/info');
    return r.data;
  },
  async dbHistory() {
    const r = await api.get('/admin/ops/backups/db/history');
    return r.data;
  },
  // R2 Backups
  async r2Info() {
    const r = await api.get('/admin/ops/backups/r2/info');
    return r.data;
  },
  async r2Snapshots() {
    const r = await api.get('/admin/ops/backups/r2/snapshots');
    return r.data;
  },
  async createR2Snapshot(prefix = '') {
    const r = await api.post('/admin/ops/backups/r2/snapshot', { prefix });
    return r.data;
  },
  async r2SnapshotStatus(taskId) {
    const r = await api.get(`/admin/ops/backups/r2/snapshot-status/${taskId}`);
    return r.data;
  },
  // Migrations
  async runMigrations() {
    const r = await api.post('/admin/ops/run-migrations');
    return r.data;
  },
  async migrationStatus() {
    const r = await api.get('/admin/ops/migration-status');
    return r.data;
  },
  // Restore
  async restoreDb(branchId, timestamp, confirmationText, useRecoveryBranch = true) {
    const r = await api.post('/admin/ops/restore/db', {
      branch_id: branchId,
      timestamp,
      confirmation_text: confirmationText,
      use_recovery_branch: useRecoveryBranch,
    });
    return r.data;
  },
  async deleteRecoveryBranch(branchId) {
    const r = await api.delete(`/admin/ops/restore/db/recovery-branch/${branchId}`);
    return r.data;
  },
  async restoreR2(snapshotId, mode, dryRun, confirmationText = '', prefixFilter = '', objectKeys = null) {
    const r = await api.post('/admin/ops/restore/r2', {
      snapshot_id: snapshotId,
      mode,
      dry_run: dryRun,
      confirmation_text: confirmationText,
      prefix_filter: prefixFilter,
      object_keys: objectKeys,
    });
    return r.data;
  },
  // Neon branches
  async neonBranches() {
    const r = await api.get('/admin/ops/neon/branches');
    return r.data;
  },
  async neonOperations(branchId = null) {
    const params = branchId ? { branch_id: branchId } : {};
    const r = await api.get('/admin/ops/neon/operations', { params });
    return r.data;
  },
};

export const maintenanceService = {
  async getStatus() {
    const r = await api.get('/maintenance/status');
    return r.data;
  },
  async getSessions() {
    const r = await api.get('/maintenance/sessions');
    return r.data;
  },
  async activate(countdownSeconds = 20, message = null) {
    const r = await api.post('/maintenance/activate', {
      countdown_seconds: countdownSeconds,
      ...(message ? { message } : {}),
    });
    return r.data;
  },
  async deactivate() {
    const r = await api.post('/maintenance/deactivate');
    return r.data;
  },
  async getEmailSettings() {
    const r = await api.get('/maintenance/email-settings');
    return r.data;
  },
  async updateEmailSettings(settings) {
    const r = await api.put('/maintenance/email-settings', settings);
    return r.data;
  },
};



// ── Suppliers Service (Phase 3) ──────────────────────────────────
export const suppliersService = {
  async list(params = {}) {
    const response = await api.get('/stocktake/suppliers', { params });
    return response.data;
  },
  async get(id) {
    const response = await api.get(`/stocktake/suppliers/${id}`);
    return response.data;
  },
  async create(data) {
    const response = await api.post('/stocktake/suppliers', data);
    return response.data;
  },
  async update(id, data) {
    const response = await api.put(`/stocktake/suppliers/${id}`, data);
    return response.data;
  },
  async delete(id) {
    await api.delete(`/stocktake/suppliers/${id}`);
  },
  async addProductLink(supplierId, data) {
    const response = await api.post(`/stocktake/suppliers/${supplierId}/products`, data);
    return response.data;
  },
  async removeProductLink(supplierId, linkId) {
    await api.delete(`/stocktake/suppliers/${supplierId}/products/${linkId}`);
  },
};

// ── Delivery Document Import Service (PDF upload + extraction review) ──
export const deliveryDocumentImportService = {
  async upload(sessionId, file) {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('file', file);
    const response = await api.post('/stocktake/delivery-documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async list(sessionId) {
    const response = await api.get('/stocktake/delivery-documents', { params: { session_id: sessionId } });
    return response.data;
  },
  async get(id) {
    const response = await api.get(`/stocktake/delivery-documents/${id}`);
    return response.data;
  },
  async getLineCandidates(lineId) {
    const response = await api.get(`/stocktake/delivery-documents/lines/${lineId}/candidates`);
    return response.data;
  },
  async getLineEvidencePills(lineId, productIds) {
    const response = await api.get(`/stocktake/delivery-documents/lines/${lineId}/evidence-pills`, {
      params: { product_ids: productIds.join(',') },
    });
    return response.data;
  },
  async getSupplierSuggestion(importId) {
    const response = await api.get(`/stocktake/delivery-documents/${importId}/supplier-suggestion`);
    return response.data;
  },
  async setReviewedSupplier(importId, supplierId) {
    const response = await api.put(`/stocktake/delivery-documents/${importId}/reviewed-supplier`, { supplier_id: supplierId });
    return response.data;
  },
  async checkSupplierName(name) {
    const response = await api.get('/stocktake/delivery-documents/supplier-name-check', { params: { name } });
    return response.data;
  },
  async setReviewedProduct(lineId, productId, { mode = 'line_only', memoryId = null, correctMemory = false, reason = null } = {}) {
    const response = await api.put(`/stocktake/delivery-documents/lines/${lineId}/reviewed-product`, {
      product_id: productId, mode, memory_id: memoryId, correct_memory: correctMemory, reason,
    });
    return response.data;
  },
  async getRememberedMatches(importId) {
    const response = await api.get(`/stocktake/delivery-documents/${importId}/remembered-matches`);
    return response.data;
  },
  async discard(id) {
    await api.delete(`/stocktake/delivery-documents/${id}`);
  },
};

// ── Deliveries Service (Phase 3) ─────────────────────────────────
export const deliveriesService = {
  async list(params = {}) {
    const response = await api.get('/stocktake/deliveries', { params });
    return response.data;
  },
  async get(id) {
    const response = await api.get(`/stocktake/deliveries/${id}`);
    return response.data;
  },
  async create(data) {
    const response = await api.post('/stocktake/deliveries', data);
    return response.data;
  },
  async update(id, data) {
    const response = await api.put(`/stocktake/deliveries/${id}`, data);
    return response.data;
  },
  async delete(id) {
    await api.delete(`/stocktake/deliveries/${id}`);
  },
  async saveLines(deliveryId, lines) {
    const response = await api.put(`/stocktake/deliveries/${deliveryId}/lines`, { lines });
    return response.data;
  },
  async post(id) {
    const response = await api.post(`/stocktake/deliveries/${id}/post`);
    return response.data;
  },
  async unpost(id) {
    const response = await api.post(`/stocktake/deliveries/${id}/unpost`);
    return response.data;
  },
  async lastCost(productId, siteId, supplierId) {
    const params = { product_id: productId, site_id: siteId };
    if (supplierId) params.supplier_id = supplierId;
    const response = await api.get('/stocktake/deliveries/last-cost', { params });
    return response.data;
  },
  async openingStockCost(productId, siteId) {
    const params = { product_id: productId, site_id: siteId };
    const response = await api.get('/stocktake/deliveries/opening-stock-cost', { params });
    return response.data;
  },
  // iter363e — Bulk delivery cost cascade for all site products.
  // iter426 (Iteration B #7) — Optional supplier_id makes the
  // last-delivery lookup supplier-scoped ORG-WIDE so a cost
  // delivered to Bar 1 yesterday cascades to Bar 2 today.
  // Replaces 100+ per-product round-trips when opening a delivery note.
  // iter_postmix — `beforeDate` (ISO YYYY-MM-DD) limits the cascade to deliveries
  // before that date, so a new delivery only pulls from past costs.
  async siteCostMap(siteId, supplierId, beforeDate) {
    const params = { site_id: siteId };
    if (supplierId) params.supplier_id = supplierId;
    if (beforeDate) params.before_date = beforeDate;
    const response = await api.get('/stocktake/deliveries/site-cost-map', { params });
    return response.data;
  },
  // iter825 — Manual DN unit-cost default: strict, site-only (never
  // cross-client), DN-line-only (never WAC/opening-stock/credit),
  // exact-purchase-unit-only (never a cross-unit conversion) source for
  // "selecting a product on a new Delivery Note". See backend module
  // note in app/routes/deliveries.py above get_dn_unit_cost().
  async dnUnitCost(productId, siteId, unitId, unitLabel, beforeDate) {
    const params = { product_id: productId, site_id: siteId };
    if (unitId) params.unit_id = unitId;
    if (unitLabel) params.unit_label = unitLabel;
    if (beforeDate) params.before_date = beforeDate;
    const response = await api.get('/stocktake/deliveries/dn-unit-cost', { params });
    return response.data;
  },
  async dnSiteCostMap(siteId, beforeDate) {
    const params = { site_id: siteId };
    if (beforeDate) params.before_date = beforeDate;
    const response = await api.get('/stocktake/deliveries/dn-site-cost-map', { params });
    return response.data;
  },
};

// ── Credits Service (Phase 3) ────────────────────────────────────
export const creditsService = {
  async list(params = {}) {
    const response = await api.get('/stocktake/credits', { params });
    return response.data;
  },
  async get(id) {
    const response = await api.get(`/stocktake/credits/${id}`);
    return response.data;
  },
  async create(data) {
    const response = await api.post('/stocktake/credits', data);
    return response.data;
  },
  async update(id, data) {
    const response = await api.put(`/stocktake/credits/${id}`, data);
    return response.data;
  },
  async delete(id) {
    await api.delete(`/stocktake/credits/${id}`);
  },
  async saveLines(creditId, lines) {
    const response = await api.put(`/stocktake/credits/${creditId}/lines`, { lines });
    return response.data;
  },
  async post(id) {
    const response = await api.post(`/stocktake/credits/${id}/post`);
    return response.data;
  },
  async unpost(id) {
    const response = await api.post(`/stocktake/credits/${id}/unpost`);
    return response.data;
  },
};


// ── Sales Service (Phase 3 standalone) ──────────────────────────
export const salesService = {
  async list(params = {}) {
    const response = await api.get('/stocktake/sales', { params });
    return response.data;
  },
  async get(id) {
    const response = await api.get(`/stocktake/sales/${id}`);
    return response.data;
  },
  async create(data) {
    const response = await api.post('/stocktake/sales', data);
    return response.data;
  },
  async update(id, data) {
    const response = await api.put(`/stocktake/sales/${id}`, data);
    return response.data;
  },
  async delete(id) {
    await api.delete(`/stocktake/sales/${id}`);
  },
  async saveLines(saleId, lines) {
    const response = await api.put(`/stocktake/sales/${saleId}/lines`, { lines });
    return response.data;
  },
  async post(id) {
    const response = await api.post(`/stocktake/sales/${id}/post`);
    return response.data;
  },
  async unpost(id) {
    const response = await api.post(`/stocktake/sales/${id}/unpost`);
    return response.data;
  },
};


// ── Stocktaking Settings Service ─────────────────────
export const stocktakingSettingsService = {
  async get() {
    const response = await api.get('/stocktake/settings');
    return response.data;
  },
  async save(data) {
    const response = await api.put('/stocktake/settings', data);
    return response.data;
  },
  async update(data) {
    const response = await api.put('/stocktake/settings', data);
    return response.data;
  },
};

// ── Stocktake Attachment Helpers ─────────────────────
export const stocktakeAttachmentService = {
  async uploadDelivery(deliveryId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/stocktake/deliveries/${deliveryId}/attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async removeDelivery(deliveryId) {
    const response = await api.delete(`/stocktake/deliveries/${deliveryId}/attachment`);
    return response.data;
  },
  async downloadDeliveryAttachment(deliveryId) {
    const response = await api.get(`/stocktake/deliveries/${deliveryId}/attachment/download`, {
      responseType: 'blob',
    });
    return response;
  },
  async uploadCredit(creditId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/stocktake/credits/${creditId}/attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async removeCredit(creditId) {
    const response = await api.delete(`/stocktake/credits/${creditId}/attachment`);
    return response.data;
  },
  async downloadCreditAttachment(creditId) {
    const response = await api.get(`/stocktake/credits/${creditId}/attachment/download`, {
      responseType: 'blob',
    });
    return response;
  },
};

// ── Supplier Product Links ───────────────────────────
export const supplierProductsService = {
  async getLinkedProducts(supplierId) {
    const response = await api.get(`/stocktake/supplier-products/${supplierId}`);
    return response.data;
  },
};

// ── Product Units ────────────────────────────────────
export const productUnitsService = {
  async getForProduct(productId) {
    const response = await api.get(`/stocktake/product-units/${productId}`);
    return response.data;
  },
};


// ── Wave 5A P4 — Start a new stocktake ──────────────
export const newStocktakeService = {
  async preflight(siteId) {
    const response = await api.get('/stocktake/new-stocktake/preflight', { params: { site_id: siteId } });
    return response.data;
  },
  async create(siteId, idempotencyKey) {
    const response = await api.post('/stocktake/new-stocktake', { site_id: siteId, idempotency_key: idempotencyKey });
    return response.data;
  },
};

// ── Wave 5A P5.4 — Add a completed historical stocktake (Admin-only) ──────────────
// Reuses the existing Stage 1 admin lookups (allow-listed sites, decoded archives) plus
// the generic orchestration endpoints — never duplicates any PSB stage logic.
export const historicalConversionService = {
  async listSites() {
    const response = await api.get('/admin/psb-stage1/sites');
    return response.data;
  },
  async listArchives(targetSiteId) {
    const params = targetSiteId ? { target_site_id: targetSiteId } : {};
    const response = await api.get('/admin/psb-stage1/stage0-sources', { params });
    return response.data;
  },
  async uploadSource(file, onProgress, house) {
    const formData = new FormData();
    formData.append('file', file);
    if (house) formData.append('house', house);
    const response = await api.post('/admin/psb-stage1/upload-source', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
    return response.data;
  },
  async getSiteProposal(stage0RunId) {
    const response = await api.get(`/admin/psb-stage1/stage0-sources/${stage0RunId}/site-proposal`);
    return response.data;
  },
  async linkSiteToHouse(stage0RunId, siteId) {
    const response = await api.post('/admin/psb-stage1/site-house-links', {
      stage0_run_id: stage0RunId, site_id: siteId, create_missing_locations: true,
    });
    return response.data;
  },
  async createSiteFromSource(body) {
    const response = await api.post('/admin/psb-stage1/sites-from-source', body);
    return response.data;
  },
  async savePriceInput(conversionId, bpId, body) {
    const response = await api.put(`/legacy-closing-snapshots/${conversionId}/price-inputs/${bpId}`, body);
    return response.data;
  },
  async clearPriceInput(conversionId, bpId) {
    const response = await api.delete(`/legacy-closing-snapshots/${conversionId}/price-inputs/${bpId}`);
    return response.data;
  },
  async preflight(targetSiteId, stage0RunId) {
    const response = await api.get('/stocktake/historical-import/preflight', {
      params: { target_site_id: targetSiteId, stage0_run_id: stage0RunId },
    });
    return response.data;
  },
  async start(targetSiteId, stage0RunId, importRoute = 'route_b') {
    const response = await api.post('/stocktake/historical-import', {
      target_site_id: targetSiteId,
      stage0_run_id: stage0RunId,
      import_route: importRoute,
    });
    return response.data;
  },
  async getStatus(conversionId) {
    const response = await api.get(`/stocktake/historical-import/${conversionId}`);
    return response.data;
  },
  async listS2Plans() {
    const response = await api.get('/admin/psb-stage2/plans');
    return response.data;
  },
  async seedDraftFromS2(planId) {
    const response = await api.post(`/admin/psb-stage2/plans/${planId}/seed-draft-session`);
    return response.data;
  },
  async previewOpeningStock(conversionId) {
    const response = await api.get(`/stocktake/historical-import/${conversionId}/plan-opening-stock/preview`);
    return response.data;
  },
  async planOpeningStock(conversionId) {
    const response = await api.post(`/stocktake/historical-import/${conversionId}/plan-opening-stock`);
    return response.data;
  },
  async startPipeline(s1RunId) {
    const response = await api.post('/stocktake/historical-import/pipeline/start', {
      s1_run_id: s1RunId,
    });
    return response.data;
  },
  async getPipelineStatus(s1RunId) {
    const response = await api.get(`/stocktake/historical-import/pipeline/status/${s1RunId}`);
    return response.data;
  },
  async getClosingSnapshot(conversionId) {
    const response = await api.get(`/legacy-closing-snapshots/${conversionId}`);
    return response.data;
  },
  async getSnapshotSourceBinding(conversionId) {
    const response = await api.get(`/legacy-closing-snapshots/${conversionId}/source-binding`);
    return response.data;
  },
  async reattachSnapshotSource(conversionId, file) {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/legacy-closing-snapshots/${conversionId}/reattach-source`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
    });
    return response.data;
  },
  async saveClosingSnapshot(conversionId, action, reviewStep, gapsAcknowledged) {
    const response = await api.post(`/legacy-closing-snapshots/${conversionId}`, {
      action, review_step: reviewStep, gaps_acknowledged: !!gapsAcknowledged,
    });
    return response.data;
  },
};

// ── Stocktake Sessions (Phase 3A) ───────────────────
export const stocktakeSessionsService = {
  async lookupProductCost(productId, siteId, method = 'weighted_average', asOfDate = null) {
    const params = { product_id: productId, site_id: siteId, method };
    if (asOfDate) params.as_of_date = asOfDate;
    const response = await api.get('/stocktake/product-cost', { params });
    return response.data;
  },
  async list(params = {}) {
    const response = await api.get('/stocktake/sessions', { params });
    return response.data;
  },
  async get(sessionId) {
    const response = await api.get(`/stocktake/sessions/${sessionId}`);
    return response.data;
  },
  async create(data) {
    const response = await api.post('/stocktake/sessions', data);
    return response.data;
  },
  async update(sessionId, data) {
    const response = await api.put(`/stocktake/sessions/${sessionId}`, data);
    return response.data;
  },
  async changeStatus(sessionId, status, options = {}) {
    const response = await api.put(`/stocktake/sessions/${sessionId}/status`, {
      status,
      force_price_override: options.forcePriceOverride || false,
      force_cost_basis_override: options.forceCostBasisOverride || false,
      override_reason: options.overrideReason || null,
    });
    return response.data;
  },
  async saveEntries(sessionId, entries) {
    const response = await api.put(`/stocktake/sessions/${sessionId}/entries`, { entries });
    return response.data;
  },
  // P3 — Scope B guarded launch: retry-safe linked Summary from this
  // locked session's exact saved calculation. Gated 503 until the CTO
  // approves/applies the Scope B migration.
  async launchSummary(sessionId, acknowledgeNearest = false) {
    const response = await api.post(`/stocktake/sessions/${sessionId}/launch-summary`, {
      acknowledge_nearest: acknowledgeNearest,
    });
    return response.data;
  },
  // iter_fix3 — set site-level default count unit for a product
  async updateCountUnitPref(sessionId, productId, countUnitId) {
    const response = await api.patch(`/stocktake/sessions/${sessionId}/count-unit-pref`, {
      product_id: productId,
      count_unit_id: countUnitId ?? null,
    });
    return response.data;
  },
  // iter_fix3_v2 — update the global product-master default count unit
  async updateGlobalCountUnit(productId, countUnitId) {
    const response = await api.patch(`/stocktake/products/${productId}/count-unit-global`, {
      count_unit_id: countUnitId,
    });
    return response.data;
  },
  async delete(sessionId) {
    const response = await api.delete(`/stocktake/sessions/${sessionId}`);
    return response.data;
  },
  async getLastSessionDate(siteId) {
    const response = await api.get('/stocktake/sessions/last-date', { params: { site_id: siteId } });
    return response.data;
  },
  // Historical Stocktake Conversion wave — admin dry-run fixture preview
  async getFixturePreview() {
    const response = await api.get('/stocktake/admin/fixture-sessions/preview');
    return response.data;
  },
  // Historical Stocktake Conversion wave — resumable import drafts
  async listImportDrafts() {
    const response = await api.get('/stocktake/historical-import/drafts');
    return response.data;
  },
  async voidImportDraft(conversionId, reason) {
    const response = await api.delete(`/stocktake/historical-import/${conversionId}`, {
      data: { reason: reason || '' }
    });
    return response.data;
  },
  async calculate(sessionId, costMethod = 'weighted_average') {
    const response = await api.post(`/stocktake/sessions/${sessionId}/calculate`, { cost_method: costMethod });
    return response.data;
  },
  async calculateAudit(sessionId, costMethod = 'weighted_average') {
    const response = await api.post(`/stocktake/sessions/${sessionId}/calculate-audit`, { cost_method: costMethod });
    return response.data;
  },
  async getCalculation(sessionId) {
    const response = await api.get(`/stocktake/sessions/${sessionId}/calculation`);
    return response.data;
  },
  async getOpeningStock(sessionId) {
    const response = await api.get(`/stocktake/sessions/${sessionId}/opening-stock`);
    return response.data;
  },
  async repopulateOpeningStock(sessionId) {
    const response = await api.post(`/stocktake/sessions/${sessionId}/opening-stock/repopulate`);
    return response.data;
  },
  async saveManualOpeningStock(sessionId, items) {
    const response = await api.post(`/stocktake/sessions/${sessionId}/opening-stock/manual`, { items });
    return response.data;
  },
  async getSales(sessionId) {
    const response = await api.get(`/stocktake/sessions/${sessionId}/sales`);
    return response.data;
  },
  async getSalesConsumption(sessionId) {
    const response = await api.get(`/stocktake/sessions/${sessionId}/sales-consumption`);
    return response.data;
  },
  async saveAccountsSales(sessionId, payload) {
    const response = await api.put(`/stocktake/sessions/${sessionId}/accounts-sales`, payload);
    return response.data;
  },
  async dedupeSessionEntries(sessionId, dryRun = true) {
    const response = await api.post(`/stocktake/sessions/${sessionId}/dedupe-entries?dry_run=${dryRun}`);
    return response.data;
  },
  // iter379 — refresh stale product names / unit labels / missing
  // costs on the session's count sheet from live master data.
  async healSessionSnapshots(sessionId) {
    const response = await api.post(`/stocktake/sessions/${sessionId}/heal-snapshots`);
    return response.data;
  },
  async auditDuplicateEntries() {
    const response = await api.get('/stocktake/dedupe-audit');
    return response.data;
  },
  async saveSales(sessionId, entries) {
    const response = await api.put(`/stocktake/sessions/${sessionId}/sales`, { entries });
    return response.data;
  },
  async getAdjustments(sessionId, type = null) {
    const params = type ? { adjustment_type: type } : {};
    const response = await api.get(`/stocktake/sessions/${sessionId}/adjustments`, { params });
    return response.data;
  },
  async saveAdjustments(sessionId, entries, forceClear = false) {
    // iter360b — pass `force_clear` so the backend can distinguish a real
    // "user cleared every row" save from an accidental autosave of an
    // empty/uninitialised UI state (which would otherwise wipe data).
    const response = await api.put(`/stocktake/sessions/${sessionId}/adjustments`, {
      entries, force_clear: forceClear,
    });
    return response.data;
  },
  async savePluAllowances(sessionId, entries) {
    // P1D — scoped save for plu_allowance investigative entries.
    // Does NOT touch wastage / allowance / management rows.
    const response = await api.put(`/stocktake/sessions/${sessionId}/plu-allowances`, { entries });
    return response.data;
  },
  async getCostingReport(sessionId) {
    const response = await api.get('/stocktake/reports/costing', { params: { session_id: sessionId } });
    return response.data;
  },
  async validateSession(sessionId) {
    const response = await api.post(`/stocktake/sessions/${sessionId}/validate`);
    return response.data;
  },
  async preCalculateCheck(sessionId) {
    const response = await api.get(`/stocktake/sessions/${sessionId}/pre-calculate-check`);
    return response.data;
  },
  async postAllDrafts(sessionId) {
    const response = await api.post(`/stocktake/sessions/${sessionId}/post-all-drafts`);
    return response.data;
  },
  async getSessionKpis(sessionId) {
    const response = await api.get(`/stocktake/sessions/${sessionId}/kpis`);
    return response.data;
  },
  // ── Phase 3: Wizard Draft Persistence ──
  async getWizardDrafts(sessionId) {
    const response = await api.get(`/stocktake/sessions/${sessionId}/wizard-draft`);
    return response.data;
  },
  async getWizardStepDraft(sessionId, stepKey) {
    const response = await api.get(`/stocktake/sessions/${sessionId}/wizard-draft/${stepKey}`);
    return response.data;
  },
  async saveWizardDraft(sessionId, stepKey, draft, stepIndex, positionStepKey) {
    // iter360b — `stepKey` identifies whose draft is being saved (for the
    // closing step's entries payload). `stepIndex` + `positionStepKey`
    // record the wizard's last-visited step (where the user is going to).
    // When `positionStepKey` is omitted we fall back to `stepKey`.
    const response = await api.put(`/stocktake/sessions/${sessionId}/wizard-draft/${stepKey}`, {
      draft, step_index: stepIndex, position_step_key: positionStepKey || stepKey,
    });
    return response.data;
  },
};

// ── Multi-Location Audit Service ──────────────
export const multiLocationService = {
  async listLocations(siteId, includeInactive = false) {
    const response = await api.get('/stocktake/multi-location/locations', {
      params: { site_id: siteId, include_inactive: includeInactive }
    });
    return response.data;
  },
  async createLocation(data) {
    const response = await api.post('/stocktake/multi-location/locations', data);
    return response.data;
  },
  async updateLocation(locationId, data) {
    const response = await api.put(`/stocktake/multi-location/locations/${locationId}`, data);
    return response.data;
  },
  async deleteLocation(locationId) {
    await api.delete(`/stocktake/multi-location/locations/${locationId}`);
  },
  async enableMultiLocation(sessionId, data) {
    const response = await api.post(`/stocktake/multi-location/sessions/${sessionId}/enable-multi-location`, data);
    return response.data;
  },
  async listChildSessions(sessionId) {
    const response = await api.get(`/stocktake/multi-location/sessions/${sessionId}/children`);
    return response.data;
  },
  async listTransfers(parentSessionId) {
    const response = await api.get('/stocktake/multi-location/transfers', {
      params: { parent_session_id: parentSessionId }
    });
    return response.data;
  },
  async createTransfer(data) {
    const response = await api.post('/stocktake/multi-location/transfers', data);
    return response.data;
  },
  async postTransfer(transferId) {
    const response = await api.post(`/stocktake/multi-location/transfers/${transferId}/post`);
    return response.data;
  },
  async getTransfer(transferId) {
    const response = await api.get(`/stocktake/multi-location/transfers/${transferId}`);
    return response.data;
  },
  async updateTransfer(transferId, data) {
    const response = await api.put(`/stocktake/multi-location/transfers/${transferId}`, data);
    return response.data;
  },
  async deleteTransfer(transferId) {
    await api.delete(`/stocktake/multi-location/transfers/${transferId}`);
  },
  // iter428 — Un-post a posted transfer back to draft so the auditor
  // can edit / delete it from the wizard. The backend enforces that
  // the parent stocktake session is still editable.
  async unpostTransfer(transferId) {
    const response = await api.post(`/stocktake/multi-location/transfers/${transferId}/unpost`);
    return response.data;
  },
  async getConsolidatedReview(parentSessionId) {
    const response = await api.get(`/stocktake/multi-location/sessions/${parentSessionId}/consolidated-review`);
    return response.data;
  },
  // iter425 — Per-product aggregated rollup across all children of a
  // parent multi-loc session. Powers the per-PRODUCT drill-down in
  // the new Calculate & Results breakdown dialog.
  async getProductsRollup(parentSessionId) {
    const response = await api.get(`/stocktake/multi-location/sessions/${parentSessionId}/products-rollup`);
    return response.data;
  },
  // iter440 — Sales-side per-product breakdown across all children
  // (Estimated Receipts: consumption × selling price). Returns one
  // flat_row per (product, location) so the dialog can group by
  // subcategory and filter by location with subtotals.
  async getConsolidatedBreakdown(parentSessionId, tile = 'estimated_receipts') {
    const response = await api.get(
      `/stocktake/multi-location/sessions/${parentSessionId}/consolidated-breakdown`,
      { params: { tile } },
    );
    return response.data;
  },
  async getLocationTransfers(locationId, parentSessionId) {
    const response = await api.get('/stocktake/multi-location/location-transfers', {
      params: { location_id: locationId, parent_session_id: parentSessionId }
    });
    return response.data;
  },
  async createLocationTransfer(data) {
    const response = await api.post('/stocktake/multi-location/location-transfers', data);
    return response.data;
  },
  // iter368 — #6 Issues to Locations: calculated bulk allocation
  async getIssueSuggestions(parentSessionId) {
    const response = await api.get(`/stocktake/multi-location/sessions/${parentSessionId}/issue-suggestions`);
    return response.data;
  },
  async bulkAllocateIssuesToLocations(parentSessionId, body) {
    const response = await api.post(`/stocktake/multi-location/sessions/${parentSessionId}/issues-to-locations/bulk-allocate`, body);
    return response.data;
  },
};


// ── Site Bar Prices (iter371) ──────────────────────
export const siteBarPricesService = {
  async getReadiness(siteId) {
    const response = await api.get(`/sites/${siteId}/bar-prices/readiness`);
    return response.data;
  },
  async getPrices(siteId, auditLocationId = null) {
    const params = auditLocationId ? { audit_location_id: auditLocationId } : {};
    const response = await api.get(`/sites/${siteId}/bar-prices`, { params });
    return response.data;
  },
  async batchSave(siteId, body) {
    const response = await api.post(`/sites/${siteId}/bar-prices/batch`, body);
    return response.data;
  },
  async copyBetweenLocations(siteId, body) {
    const response = await api.post(`/sites/${siteId}/bar-prices/copy`, body);
    return response.data;
  },
  async getEffectiveSegments(siteId, { periodFrom, periodTo, auditLocationId = null }) {
    const params = { period_from: periodFrom, period_to: periodTo };
    if (auditLocationId) params.audit_location_id = auditLocationId;
    const response = await api.get(`/sites/${siteId}/bar-prices/effective-segments`, { params });
    return response.data;
  },
};


// ── Costing Policy (Phase 3 hardening) ──────────────
export const costingPolicyService = {
  async getMethods() {
    const response = await api.get('/stocktake/costing-policy/methods');
    return response.data;
  },
  async getGlobal() {
    const response = await api.get('/stocktake/costing-policy/global');
    return response.data;
  },
  async setGlobal(data) {
    const response = await api.put('/stocktake/costing-policy/global', data);
    return response.data;
  },
  async getGroupPolicy(clientId) {
    const response = await api.get(`/stocktake/costing-policy/group/${clientId}`);
    return response.data;
  },
  async setGroupPolicy(clientId, data) {
    const response = await api.put(`/stocktake/costing-policy/group/${clientId}`, data);
    return response.data;
  },
  async removeGroupPolicy(clientId) {
    const response = await api.delete(`/stocktake/costing-policy/group/${clientId}`);
    return response.data;
  },
  async getSitePolicy(siteId) {
    const response = await api.get(`/stocktake/costing-policy/site/${siteId}`);
    return response.data;
  },
  async setSitePolicy(siteId, data) {
    const response = await api.put(`/stocktake/costing-policy/site/${siteId}`, data);
    return response.data;
  },
  async removeSitePolicy(siteId) {
    const response = await api.delete(`/stocktake/costing-policy/site/${siteId}`);
    return response.data;
  },
  async getEffective(siteId) {
    const response = await api.get(`/stocktake/costing-policy/effective?site_id=${siteId}`);
    return response.data;
  },
  async getAllPolicies() {
    const response = await api.get('/stocktake/costing-policy/all');
    return response.data;
  },
};

export const measureDefinitionsService = {
  async list() {
    const response = await api.get('/stocktake/measure-definitions');
    return response.data;
  },
  async create(data) {
    const response = await api.post('/stocktake/measure-definitions', data);
    return response.data;
  },
  async update(id, data) {
    const response = await api.put(`/stocktake/measure-definitions/${id}`, data);
    return response.data;
  },
  async remove(id) {
    await api.delete(`/stocktake/measure-definitions/${id}`);
  },
};

// Management Digest (Scheduled Insights) rules
export const managementDigestService = {
  async listRules(signal) {
    const response = await api.get('/management-digest-rules', { signal });
    return response.data;
  },
  async listRecipients(signal) {
    const response = await api.get('/management-digest-rules/recipients', { signal });
    return response.data;
  },
  async createRule(data) {
    const response = await api.post('/management-digest-rules', data);
    return response.data;
  },
  async updateRule(ruleId, data) {
    const response = await api.put(`/management-digest-rules/${ruleId}`, data);
    return response.data;
  },
  async deleteRule(ruleId) {
    const response = await api.delete(`/management-digest-rules/${ruleId}`);
    return response.data;
  },
  async sendNow(ruleId) {
    const response = await api.post(`/management-digest-rules/${ruleId}/send-now`);
    return response.data;
  },
  async getLogs(ruleId) {
    const response = await api.get(`/management-digest-rules/${ruleId}/logs`);
    return response.data;
  },
};

// Section Master Items — Allowances / Management / Adjustment dropdown lists
export const sectionMasterItemsService = {
  async list(category, activeOnly = false) {
    const response = await api.get('/master-data/section-items', { params: { category, active_only: activeOnly } });
    return response.data;
  },
  async create(category, name) {
    const response = await api.post('/master-data/section-items', { category, name });
    return response.data;
  },
  async update(id, data) {
    const response = await api.put(`/master-data/section-items/${id}`, data);
    return response.data;
  },
  async remove(id) {
    await api.delete(`/master-data/section-items/${id}`);
  },
};



export const actionPlansService = {
  async list(params = {}) {
    const response = await api.get('/action-plans', { params });
    return response.data;
  },
  async get(planId) {
    const response = await api.get(`/action-plans/${planId}`);
    return response.data;
  },
  async create(data) {
    const response = await api.post('/action-plans', data);
    return response.data;
  },
  async update(planId, data) {
    const response = await api.patch(`/action-plans/${planId}`, data);
    return response.data;
  },
  async getCarryForwardCandidates(siteId, scope) {
    const response = await api.get('/action-plans/carry-forward', { params: { site_id: siteId, scope } });
    return response.data;
  },
  // Items
  async addItem(planId, data) {
    const response = await api.post(`/action-plans/${planId}/items`, data);
    return response.data;
  },
  async updateItem(planId, itemId, data) {
    const response = await api.patch(`/action-plans/${planId}/items/${itemId}`, data);
    return response.data;
  },
  async deleteItem(planId, itemId) {
    const response = await api.delete(`/action-plans/${planId}/items/${itemId}`);
    return response.data;
  },
  async carryForwardItems(planId, sourceItemIds) {
    const response = await api.post(`/action-plans/${planId}/items/carry-forward`, { source_item_ids: sourceItemIds });
    return response.data;
  },
  // Corrective actions
  async addCorrectiveAction(planId, itemId, data) {
    const response = await api.post(`/action-plans/${planId}/items/${itemId}/corrective-actions`, data);
    return response.data;
  },
  async updateCorrectiveAction(planId, itemId, caId, data) {
    const response = await api.patch(`/action-plans/${planId}/items/${itemId}/corrective-actions/${caId}`, data);
    return response.data;
  },
  async deleteCorrectiveAction(planId, itemId, caId) {
    const response = await api.delete(`/action-plans/${planId}/items/${itemId}/corrective-actions/${caId}`);
    return response.data;
  },
  // Factor lines
  async addFactorLine(planId, itemId, data) {
    const response = await api.post(`/action-plans/${planId}/items/${itemId}/factor-lines`, data);
    return response.data;
  },
  async updateFactorLine(planId, itemId, flId, data) {
    const response = await api.patch(`/action-plans/${planId}/items/${itemId}/factor-lines/${flId}`, data);
    return response.data;
  },
  async deleteFactorLine(planId, itemId, flId) {
    const response = await api.delete(`/action-plans/${planId}/items/${itemId}/factor-lines/${flId}`);
    return response.data;
  },
  // Monitoring
  async monitoringSummary(params = {}) {
    const response = await api.get('/action-plans/monitoring/summary', { params });
    return response.data;
  },
  async caReport(params = {}) {
    const response = await api.get('/action-plans/ca-report', { params });
    return response.data;
  },
  caReportCsvUrl(params = {}) {
    // Returns the full URL for direct browser download — same scoping and
    // filtering as caReport(); screen totals and CSV are derived from the
    // same row set server-side.
    const base = api.defaults.baseURL || '';
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return `${base}/action-plans/ca-report/export.csv${qs ? '?' + qs : ''}`;
  },
  async deletePlan(planId, reason) {
    await api.delete(`/action-plans/${planId}/delete-clean`, { data: { reason } });
  },
  async downloadPdf(planId) {
    const response = await api.get(`/action-plans/${planId}/pdf`, { responseType: 'blob' });
    return response.data;
  },
  async getNotifications(planId) {
    const response = await api.get(`/action-plans/${planId}/notifications`);
    return response.data;
  },
  async getNotificationStats() {
    const response = await api.get('/action-plans/notification-stats');
    return response.data;
  },
  // Draft AP: publish-preview + publish (Items 2/9)
  async getPublishPreview(planId) {
    const response = await api.get(`/action-plans/${planId}/publish-preview`);
    return response.data;
  },
  async publishDraft(planId) {
    const response = await api.post(`/action-plans/${planId}/publish`);
    return response.data;
  },
  // Prior AP review (Item 7)
  async getPriorReview(params = {}) {
    const response = await api.get('/action-plans/prior-review', { params });
    return response.data;
  },
  // Config
  async getClientConfigs(clientId) {
    const response = await api.get(`/action-plan-configs/${clientId}`);
    return response.data;
  },
  async upsertClientConfig(clientId, scope, data) {
    const response = await api.put(`/action-plan-configs/${clientId}/${scope}`, data);
    return response.data;
  },
};

// ── Client Portal Action Plan Service ─────────────────────────────────────────
export const clientActionPlanService = {
  async list(params = {}) {
    const response = await api.get('/client/action-plans', { params });
    return response.data;
  },
  async get(planId) {
    const response = await api.get(`/client/action-plans/${planId}`);
    return response.data;
  },
  async updateItem(planId, itemId, data) {
    const response = await api.patch(`/client/action-plans/${planId}/items/${itemId}`, data);
    return response.data;
  },
  async updateCA(planId, itemId, caId, data) {
    const response = await api.patch(`/client/action-plans/${planId}/items/${itemId}/corrective-actions/${caId}`, data);
    return response.data;
  },
  async addCA(planId, itemId, data) {
    const response = await api.post(`/client/action-plans/${planId}/items/${itemId}/corrective-actions`, data);
    return response.data;
  },
  async addFactorLine(planId, itemId, data) {
    const response = await api.post(`/client/action-plans/${planId}/items/${itemId}/factor-lines`, data);
    return response.data;
  },
  async updateFactorLine(planId, itemId, flId, data) {
    const response = await api.patch(`/client/action-plans/${planId}/items/${itemId}/factor-lines/${flId}`, data);
    return response.data;
  },
  async deleteFactorLine(planId, itemId, flId) {
    const response = await api.delete(`/client/action-plans/${planId}/items/${itemId}/factor-lines/${flId}`);
    return response.data;
  },
  async downloadPdf(planId) {
    const response = await api.get(`/client/action-plans/${planId}/pdf`, { responseType: 'blob' });
    return response.data;
  },
  async submitPlan(planId) {
    const response = await api.post(`/client/action-plans/${planId}/submit`);
    return response.data;
  },
  async worklist(params = {}) {
    const response = await api.get('/client/action-plans/worklist', { params });
    return response.data;
  },
  async analytics(params = {}) {
    const response = await api.get('/client/action-plans/analytics', { params });
    return response.data;
  },
  analyticsCsvUrl(params = {}) {
    // Direct-download URL — same scoping/filtering as analytics(); screen
    // totals and the CSV are derived from the same row set server-side.
    const base = api.defaults.baseURL || '';
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return `${base}/client/action-plans/analytics/export.csv${qs ? '?' + qs : ''}`;
  },
};